import {useState} from 'react'
import {Collapse} from '@mui/material'
import {KeyboardArrowRight, KeyboardArrowDown} from '@mui/icons-material'
import {styled} from 'common'

const MAX_DEPTH = 10

const isHtml = (value) => {
    if (typeof value !== 'string') return false
    return /<[a-z][\s\S]*>/i.test(value)
}

const getPreviewSummary = (obj) => {
    const keys = Object.keys(obj)
    const preview = keys
        .slice(0, 3)
        .map((k) => {
            const v = obj[k]
            if (v === null || v === undefined) return `${k}: null`
            if (typeof v === 'object') return `${k}: {…}`
            const str = String(v)
            return `${k}: ${str.length > 20 ? str.slice(0, 20) + '…' : str}`
        })
        .join('  ·  ')
    return keys.length > 3 ? `${preview}  · +${keys.length - 3} more` : preview
}

const HtmlPreview = ({html}) => {
    const [open, setOpen] = useState(false)
    const lineCount = html.split('\n').length

    return (
        <NestedWrapper>
            <NestedHeader onClick={() => setOpen((p) => !p)}>
                <ChevronIcon>
                    {open ? (
                        <KeyboardArrowDown fontSize="inherit" />
                    ) : (
                        <KeyboardArrowRight fontSize="inherit" />
                    )}
                </ChevronIcon>
                <NestedLabel>HTML</NestedLabel>
                <NestedMeta>{lineCount} lines</NestedMeta>
            </NestedHeader>
            <Collapse in={open} unmountOnExit>
                <NestedBody>
                    <HtmlCodeBlock>{html}</HtmlCodeBlock>
                    <HtmlDivider />
                    <HtmlFrame>
                        <div dangerouslySetInnerHTML={{__html: html}} />
                    </HtmlFrame>
                </NestedBody>
            </Collapse>
        </NestedWrapper>
    )
}

const ArrayValue = ({value, depth}) => {
    if (value.length === 0) return <Value>[]</Value>
    return (
        <Value>
            {value.map((item, i) => {
                if (item !== null && typeof item === 'object') {
                    return <NestedValue key={i} value={item} depth={depth} label={`[${i}]`} />
                }
                return <ArrayItem key={i}>{String(item)}</ArrayItem>
            })}
        </Value>
    )
}

const ScalarValue = ({value}) => {
    if (isHtml(value)) {
        return (
            <Value>
                <HtmlPreview html={value} />
            </Value>
        )
    }
    return <Value>{value === null || value === undefined ? 'null' : String(value)}</Value>
}

const NestedValue = ({value, depth, label}) => {
    const [open, setOpen] = useState(false)
    const keyCount = Object.keys(value).length
    const summary = getPreviewSummary(value)

    return (
        <NestedWrapper>
            <NestedHeader onClick={() => setOpen((p) => !p)} open={open}>
                <ChevronIcon>
                    {open ? (
                        <KeyboardArrowDown fontSize="inherit" />
                    ) : (
                        <KeyboardArrowRight fontSize="inherit" />
                    )}
                </ChevronIcon>
                {label && <NestedLabel>{label}</NestedLabel>}
                <NestedMeta>
                    {keyCount} {keyCount === 1 ? 'field' : 'fields'}
                </NestedMeta>
                {!open && <NestedSummary>{summary}</NestedSummary>}
            </NestedHeader>
            <Collapse in={open} unmountOnExit>
                <NestedBody>
                    <KeyValue object={value} depth={depth + 1} />
                </NestedBody>
            </Collapse>
        </NestedWrapper>
    )
}

const KeyValue = ({object, depth = 0}) => {
    if (!object || typeof object !== 'object') return null
    if (depth > MAX_DEPTH)
        return (
            <Row depth={depth}>
                <Value>[max depth reached]</Value>
            </Row>
        )

    return Object.keys(object).map((key) => {
        const value = object[key]
        const isNested = value !== null && typeof value === 'object' && !Array.isArray(value)
        const isArray = Array.isArray(value)

        return (
            <Row key={key} depth={depth}>
                {isNested && <NestedValue value={value} depth={depth} label={key} />}
                {isArray && (
                    <>
                        <Key>{`${key}:`}</Key>
                        <ArrayValue value={value} depth={depth} />
                    </>
                )}
                {!isNested && !isArray && (
                    <>
                        <Key>{`${key}:`}</Key>
                        <ScalarValue value={value} />
                    </>
                )}
            </Row>
        )
    })
}

export default KeyValue

const Row = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    max-width: 100%;
    padding-left: ${({depth}) => depth * 16}px;
    margin-top: 3px;
`

const Key = styled.span`
    font-weight: 600;
    white-space: nowrap;
    margin-right: 8px;
    font-size: 13px;
    color: var(--color-gray);
`

const Value = styled.span`
    font-size: 13px;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
    flex: 1;
`

/* ── Nested object ── */

const NestedWrapper = styled.div`
    width: 100%;
    margin-top: 4px;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid var(--color-light-gray);
`

const NestedHeader = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px;
    background: var(--color-background);
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
    &:hover {
        background: var(--color-light);
    }
`

const ChevronIcon = styled.span`
    display: flex;
    align-items: center;
    font-size: 16px;
    color: var(--color-gray);
    flex-shrink: 0;
`

const NestedLabel = styled.span`
    font-weight: 600;
    font-size: 13px;
    color: var(--color-dark-charcoal);
    flex-shrink: 0;
`

const NestedMeta = styled.span`
    font-size: 11px;
    color: var(--color-app-faded-grey);
    background: var(--color-light-gray);
    border-radius: 10px;
    padding: 1px 7px;
    flex-shrink: 0;
`

const NestedSummary = styled.span`
    font-size: 11px;
    color: var(--color-app-faded-grey);
    font-style: italic;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`

const NestedBody = styled.div`
    padding: 6px 10px 8px 10px;
    border-top: 1px solid var(--color-light-gray);
    /* background: var(--color-white); */
`

/* ── Array ── */

const ArrayItem = styled.div`
    font-size: 13px;
    overflow-wrap: anywhere;
    word-break: break-word;
    &::before {
        content: '• ';
        color: var(--color-app-faded-grey);
    }
`

/* ── HTML value ── */

const HtmlCodeBlock = styled.pre`
    margin: 0;
    padding: 10px 12px;
    font-family: 'Fira Code', 'Cascadia Code', Consolas, monospace;
    font-size: 12px;
    white-space: pre-wrap;
    word-break: break-all;
    overflow-y: auto;
    max-height: 220px;
    color: var(--color-dark-charcoal);
    background: var(--color-background);
`

const HtmlDivider = styled.div`
    height: 1px;
    background: var(--color-light-gray);
`

const HtmlFrame = styled.div`
    padding: 10px 12px;
    background: var(--color-white);
    color: var(--color-dark-charcoal);
    max-height: 300px;
    overflow: auto;
    font-size: 13px;
    word-break: break-word;
    overflow-wrap: anywhere;
`
